import { addMonths, setDay } from "date-fns";
import { getSubscription } from "./helpers";
import { SUBSCRIPTION_ID_ATTRIBUTE } from "./constants";

export default class VbcSelection extends HTMLElement {
  static observedAttributes = [SUBSCRIPTION_ID_ATTRIBUTE];

  constructor() {
    super();

    this.abortController = new AbortController();

    const self = this;
    this.state = new Proxy(
      {
        subscription: null,
        selection: null,
        selectionChanged: false,
        output: null,
        loading: true,
      },
      {
        set(state, key, value) {
          const oldValue = state[key];

          state[key] = value;
          if (oldValue !== value) {
            switch (key) {
              case "selectionChanged":
                self.processSelectionChangedChange();
                break;
              case "output":
                self.processOutputChange();
                break;
              case "loading":
                self.processLoadingChange();
                break;
              default:
                break;
            }
          }
          return state;
        },
      },
    );
  }

  get orderDate() {
    if (this.subscription.is_prepaid) {
      const date = new Date();
      let nextMonth = addMonths(date, 1);
      nextMonth = setDay(nextMonth, this.subscription.order_day_of_month || 1);
      return nextMonth.toISOString();
    } else {
      return this.subscription.next_charge_scheduled_at;
    }
  }

  get selectionRadios() {
    return [...this.querySelectorAll("input[type='radio']")];
  }

  get loadingOverlay() {
    return this.querySelector("#vbc-selection-loading-overlay");
  }

  get form() {
    return this.querySelector("form");
  }

  get submitButton() {
    return this.form.querySelector("button:not([type='button'])");
  }

  get output() {
    return this.querySelector("output");
  }

  get fetchPath() {
    return "/apps/verso/vbc_selection";
  }

  connectedCallback() {
    this.addEventListeners();
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  async attributeChangedCallback(name, _, value) {
    if (name === SUBSCRIPTION_ID_ATTRIBUTE && value) {
      const subscriptionId = Number(value);
      this.subscription = await getSubscription(subscriptionId);

      if (!this.subscription) {
        this.state.loading = false;
        this.state.output =
          "There was an issue fetching details about this subscription.";
      } else if (
        !(
          this.subscription.product_title?.includes("Partisan") ||
          this.subscription.product_title?.includes("Comrade")
        )
      ) {
        this.state.loading = false;
        this.state.output =
          "This subscription does not include a monthly selection.";
        this.setAttribute("data-hide-section", "true");
      } else {
        this.fetchSelection();
      }
    }
  }

  addEventListeners() {
    this.form.addEventListener("submit", this.handleFormSubmit, {
      signal: this.abortController.signal,
    });

    for (const input of this.selectionRadios) {
      input.addEventListener("change", this.handleSelectionChange, {
        signal: this.abortController.signal,
      });
    }
  }

  handleFormSubmit = (event) => {
    event.preventDefault();

    event.submitter.style.pointerEvents = "none";

    const productId = Number(this.state.selection);
    const selectedRadio = this.selectionRadios.find((input) => {
      return input.value === this.state.selection;
    });
    const isbn = Number(selectedRadio?.dataset.isbn);

    this.recordSelection(productId, isbn, () => {
      event.submitter.style.pointerEvents = null;
    });
  };

  handleSelectionChange = (event) => {
    this.state.selection = event.target.value;
    this.state.selectionChanged = true;
  };

  fetchSelection() {
    const path =
      this.fetchPath +
      "?" +
      new URLSearchParams({
        subscription_id: this.subscription.id,
        order_date: this.orderDate,
      });

    fetch(path, this.fetchConfig("GET"))
      .then((response) => response.json())
      .then((data) => {
        if (data?.shopify_product_id) {
          this.state.selection = data.shopify_product_id;

          for (const input of this.selectionRadios) {
            if (input.value === String(data.shopify_product_id)) {
              input.setAttribute("checked", "");
              break;
            }
          }
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.state.loading = false;
      });
  }

  recordSelection(productId, isbn, callback) {
    const body = JSON.stringify({
      subscription_id: this.subscription.id,
      order_date: this.orderDate,
      product_id: productId,
      isbn,
    });

    this.submitButton.innerText = "Updating…";

    fetch(this.fetchPath, { ...this.fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((data) => {
        if (data?.shopify_product_id) {
          this.state.selection = data.shopify_product_id;
          this.state.selectionChanged = false;
          this.state.output = "Your selection has been updated.";
        } else if (data?.error) {
          console.error(data.error);
          this.state.output = "There was an error updating your selection.";
        }
      })
      .catch((error) => {
        console.error(error);
        this.state.output = "There was an error updating your selection.";
      })
      .finally(() => {
        this.submitButton.innerText = "Update selection";

        if (callback && typeof callback === "function") callback();
      });
  }

  processLoadingChange() {
    this.loadingOverlay.classList.toggle("hidden", !this.state.loading);
    this.form.classList.toggle("hidden", this.state.loading);
  }

  processSelectionChangedChange() {
    if (this.state.selectionChanged) {
      this.submitButton.removeAttribute("disabled");
    } else {
      this.submitButton.setAttribute("disabled", "");
    }
  }

  processOutputChange() {
    if (!this.output) return;

    this.output.innerHTML = this.state.output || "";
    this.output.classList.toggle("hidden", !this.state.output);
  }

  fetchConfig(method = "POST") {
    return {
      method,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };
  }
}
